import { useCallback, useId, useState, type ReactElement } from 'react';
import { FormGroup } from '@ff-it/form';
import { Input, useDebounce } from '@ff-it/ui';
import { Box, Spinner } from '@ff-it/ui';
import { Icon } from 'components/Icon';
import { clearIcon } from 'styles/style.css';
import type { ActivityType } from 'types';
import {
  ChainTargetEntry,
  ProductChainPicker,
  targetFromPath,
  targetIdFromPath,
  type ChainTarget,
} from 'modules/supplier/supplier/components/ProductChainPicker';
import type { ClientTargetPath } from './types';
import type { ClientDiscountOwner } from './owner';
import type { ClientDiscountRootOwner } from '../vendorTerms/types';
import { useClientTargetDiscovery } from './targetDiscovery';
import * as styles from './ClientTargetPicker.css';

export type ClientTargetPickerProps = {
  type: ActivityType;
  owner?: ClientDiscountOwner;
  kind?: ClientDiscountRootOwner['kind'];
  value: ClientTargetPath | null;
  onChange: (value: ClientTargetPath | null) => void;
  disabled?: boolean;
};

/**
 * Client target discovery. Provider availability is deliberately
 * absent: the section provider is not a target-catalogue filter.
 */
export function ClientTargetPicker({ ...props }: ClientTargetPickerProps): ReactElement {
  // Remounting on a type switch clears the local search draft as well as the
  // discovery request state; no previous type's result is selectable.
  return (
    <ClientTargetPickerContent
      key={`${props.type}-${props.kind ?? props.owner?.kind}-${props.owner?.id ?? 'all'}`}
      {...props}
    />
  );
}

function ClientTargetPickerContent({
  type,
  owner,
  kind = owner?.kind ?? 'vendor',
  value,
  onChange,
  disabled = false,
}: ClientTargetPickerProps): ReactElement {
  const searchId = useId();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const updateSearch = useCallback((next: string) => setSearch(next), []);
  const debouncedSearch = useDebounce(updateSearch, 500);

  const { paths, targetPaths, count, returnedCount, isTruncated, isLoading, error } = useClientTargetDiscovery(
    type,
    search,
    owner,
    kind,
  );

  const selectedTarget: ChainTarget | null = value ? targetFromPath(value) : null;
  const selectedTargetIsDiscoverable = value
    ? targetPaths.some((path) => targetIdFromPath(path) === targetIdFromPath(value))
    : true;

  const handleSearchChange = (next: string) => {
    setSearchInput(next);
    if (next === '') {
      debouncedSearch.cancel();
      updateSearch('');
    } else {
      debouncedSearch(next);
    }
  };

  const handleTargetChange = (target: ChainTarget | null) => {
    if (!target) {
      onChange(null);
      return;
    }
    const selected = targetPaths.find((path) => targetIdFromPath(path) === `${target.kind}-${target.id}`);
    if (selected) onChange(selected);
  };

  return (
    <div className={styles.container} data-testid="client-target-picker">
      <FormGroup label="Target" id={searchId} className="mb-0">
        <div className={styles.search}>
          <Input
            id={searchId}
            size="sm"
            className={styles.searchInput}
            name="client-target-search"
            aria-label="Search discount targets"
            placeholder="Search discount products..."
            value={searchInput}
            disabled={disabled}
            onChange={(event) => handleSearchChange(event.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              className={styles.clearSearch}
              disabled={disabled}
              onClick={() => handleSearchChange('')}
              aria-label="Clear target search"
            >
              <Icon icon="circle-xmark" className={clearIcon} />
            </button>
          )}
        </div>
      </FormGroup>

      <ChainTargetEntry
        value={selectedTarget}
        path={value ?? undefined}
        selectionStatus={error ? 'error' : isLoading ? 'loading' : 'valid'}
        emptyMessage={kind === 'vendor' ? 'Select a vendor, outlet or product' : 'Select a service category or product'}
      />

      {!selectedTargetIsDiscoverable && value && (
        <Box textSize="xs" color="warning700" mt="1">
          The saved target is not in the current search results and remains selected.
        </Box>
      )}

      {error ? (
        <Box role="alert" color="danger" textSize="sm" mt="2">
          Unable to load discount targets.
        </Box>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" p="3" role="status">
          <Spinner />
        </Box>
      ) : (
        <div className={styles.catalogue}>
          <ProductChainPicker
            className={styles.chain}
            value={selectedTarget}
            path={value ?? undefined}
            paths={paths}
            selectableKinds={kind === 'vendor' ? ['vendor', 'outlet', 'product'] : ['service-category', 'product']}
            disabled={disabled}
            onChange={handleTargetChange}
          />
        </div>
      )}

      {isTruncated && (
        <Box color="warning700" textSize="xs" p="1.5">
          Showing a limited catalogue ({returnedCount} of {count} products). Refine your search to see other results.
        </Box>
      )}
    </div>
  );
}
